/**
 * THE GUIDE — /help/guide (design_handoff_track_polish §8).
 *
 * Two halves, and the order matters.
 *
 * FIRST, the twelve sections from the design file's `guideSrc`, verbatim: what
 * Track is FOR, read start to finish. Each is a title, one or two paragraphs, an
 * optional two-column steps table, an optional rule callout, and a pill that
 * takes you to the screen it describes — because a guide you cannot act on from
 * is a document, not a guide.
 *
 * SECOND, "Track in detail": the twelve sections Help carried before this pass.
 * They are the operational specifics — the four email keys, the TRACK// tag, the
 * file formats the viewer opens, what the registers do — and none of it is
 * repeated above. Deleting them to make room for the new copy would have been
 * the easy read of "replace Help"; they are the answers people actually come
 * here for.
 *
 * The contents list on the left covers both halves and sticks while you read.
 */
import { escapeHtml } from '../ui.js';
import { fillKeys } from '../keys.js';
import { hasKeys, shortcut } from '../keys.js';
import { GUIDE } from '../help-content.js';
import { WALKTHROUGHS } from '../help-walkthroughs.js';
import { openWalkthrough, walkthroughSecs, durationLabel } from './help-walkthrough.js';
import { canCalculate, canSee, navItem } from '../nav.js';
import { orgSetting } from '../org.js';

const SECTIONS = [
  { id: 'start', title: 'Getting started', body: `
    <p>Track lives at one address — <b>track.aspconsulting.co.uk</b>. Sign in with your ASP
    Google account and you're in. Finance users enrol a passkey (Face ID / Touch ID) the first
    time they open the Bank.</p>` },

  { id: 'email', title: 'The email keys', body: `
    <p>Track is fed by email. Learn these four and it looks after the rest.</p>
    <div class="help-keys">
      <div class="help-key"><code>enquiries@aspconsulting.co.uk</code>
        <span>New-work enquiries. Screened automatically and dropped onto the Enquiries board, or held for review.</span></div>
      <div class="help-key"><code>track@aspconsulting.co.uk</code>
        <span>The automation mailbox. Document control, issue tracking and status all run off mail sent or copied here.</span></div>
      <div class="help-key"><code>TRACK// {project}</code>
        <span>Put in the <b>subject</b> (e.g. <code>TRACK// 8299</code>) of any project email — documents file themselves and the register updates.</span></div>
      <div class="help-key"><code>RECEIPT//</code>
        <span>Forward supplier receipts to track@ with this tag — optimised, filed into Finance, and logged.</span></div>
    </div>
    <p class="help-note"><b>Forgot to tag an email?</b> Forward it to <code>track@</code> and type
    <code>TRACK// {project}</code> anywhere in the <b>body</b>. Track discards the forwarding wrapper
    and captures the original message and attachments beneath it.</p>` },

  { id: 'enquiries', title: 'Enquiries', body: `
    <p>New enquiries arrive, get spam-screened for free, and land on the <b>Enquiries</b> board.
    From an enquiry you can:</p>
    <ul>
      <li><b>Save attachments</b> — files the client's documents into a new enquiry folder and starts the register.</li>
      <li><b>Fee calculator</b> — build the fee line by line (it auto-saves); pick items from the list and the hours fill in.</li>
      <li><b>Generate fee proposal</b> — produces the branded PDF, numbered and filed, the moment you click.</li>
      <li><b>Mark Won</b> — creates the project, mints its number and builds its Drive folders. Otherwise <b>Mark Lost</b> or <b>Dismiss</b> (Spam / Error / Other).</li>
    </ul>
    <p>When the fee proposal is emailed to the client, the enquiry moves to <b>Quoted</b> on its own.</p>` },

  { id: 'projects', title: 'Projects and the golden thread', body: `
    <p>The <b>Projects</b> list is your whole portfolio — filter by scheme, status or lead, and
    search by number, client or description. Open a project for the full picture: summary, live
    status, budget vs time, fee vs invoiced, the deadline, a site map, the document registers,
    emails and the Drive folder.</p>
    <p>Everything is linked. From a project you can jump to its <b>client</b> and the <b>enquiry</b>
    it came from; from a client you see every project, enquiry and invoice attached to them. That's
    the golden thread — no dead ends.</p>
    <p><b>Quick edits:</b> click a project's <b>status</b> or <b>lead</b> right in the list to change it
    without opening the project (status only moves forward). <b>Meeting mode</b> runs the team meeting:
    pick who is in and the order, then go person by person through their projects (with what moved since
    the last meeting) and tasks. Type an action as you talk and Enter puts it on their list; give anything
    nobody owns to someone; wrap up with the actions, changes and notes — and send the notes to the team.
    The room screen and a second office can follow along. There's also an <b>ADIPS</b> filter for the inspection work.</p>` },

  { id: 'documents', title: 'Issued and received documents', body: `
    <p>Every project page carries an <b>Issued</b> and a <b>Received</b> register.</p>
    <p>Email a document to a client (an outgoing <code>TRACK//</code> email with an attachment) and
    Track records it under <b>Issued</b> and can produce an issue sheet. Documents a client sends in
    are filed into <b>2. Information → Documents Received</b> — sorted Drawings / Reports / Images by
    type — and listed under <b>Received</b>, with superseded revisions flagged automatically.</p>` },

  { id: 'drive', title: 'Google Drive and viewing files', body: `
    <p>Each project links straight to its <b>Drive folder</b>, and you can browse and open files
    <b>inside Track</b> — nothing leaves Google. Viewable formats:</p>
    <ul>
      <li><b>PDF</b> — read <em>and mark up</em> in-window; your markup saves back as a new Drive revision.
        Big file? Hit <b>Compress</b> to shrink it in place — it saves straight back to the same file
        (no download, no duplicate), and the original stays in Drive's version history.</li>
      <li><b>Drawings — DWG / DXF</b> — rendered in-app, no AutoCAD needed.</li>
      <li><b>Tekla / IFC & STEP</b> — 3D models you can rotate and inspect in-app.</li>
      <li><b>Point clouds — XYZ</b> — survey / scan clouds, orbit and inspect in 3D (large clouds are thinned for preview).</li>
      <li><b>Tedds</b> — calculation files, formatted for reading, with the diagrams and all their
        labels included. No Tedds licence needed — Track shows the saved results, it doesn't recalculate.</li>
      <li><b>Images & Office docs</b> — Word, Excel and images preview inline.</li>
    </ul>` },

  { id: 'time', title: 'Timesheets', body: `
    <p>Log time per project each week on the <b>Time</b> screen — it auto-saves as you type. Use the
    ✎ on a row to add a <b>note</b>, or tick <b>Review</b> when your entry is a review of someone
    else's work (that moves the project to Ready to Issue). Your first time entry on a project moves
    it to <b>In Progress</b>.</p>` },

  { id: 'expenses', title: 'Expenses', body: `
    <p>Claim on the <b>Expenses</b> screen: <b>mileage</b> (miles only — costed at <b>55p/mile</b>)
    or a <b>purchase</b> (amount, VAT, supplier, receipt photo, optional approved PO). Expense
    periods run the 25th → 24th. Finance reviews and approves them on the Purchase orders screen.</p>` },

  { id: 'calendar', title: 'Calendar', body: `
    <p>The <b>Calendar</b> shows your own calendar alongside the Sigma team calendar. Add meetings
    and request annual leave; reviewers approve it from the leave strip on the Calendar. Approved
    leave and bank holidays blank the day on your timesheet, and other non-project time (admin,
    training, meetings…) is logged as <b>ASP time</b> there — no placeholder project needed.</p>` },

  { id: 'finance', title: 'Finance and the Bank', body: `
    <p>Fees, invoices, Summary and the Bank are visible to <b>Coordinators and above</b> only. The
    <b>Bank</b> shows your live Revolut feed behind a Face ID / Touch ID check, re-verified each time
    you open it. Match incoming payments to invoices and outgoing to POs — money in shows as a
    <b>Sale</b>, and Track auto-suggests the matching invoice from its <code>INV</code> reference.</p>` },

  { id: 'ask', title: 'Ask Track', body: `
    <p><b>Search</b>, at the top of every screen${hasKeys() ? ` (or ${shortcut('K')})` : ''}, is a universal search
    across projects, clients, enquiries, invoices and people. Type a number, a name or a client and
    jump straight to it. Ask a complete question to search approved standards, controlled ASP
    procedures and precedent projects. Knowledge answers cite their sources and escalate when the
    approved material cannot support a safe answer.</p>` },

  { id: 'status', title: 'Status moves on its own', body: `
    <p>You rarely set status by hand — Track derives it. Time or a document → <b>In Progress</b>; a
    PDF into Calcs / Drawings / Reports → <b>Review</b>; a reviewer's flagged time → <b>Ready to
    Issue</b>; documents emailed to a client → <b>Issued / Part Issued</b> (you confirm). You can
    always click the status pill to advance it, or use the ✎.</p>` },

  { id: 'wrong', title: 'Something wrong?', body: `
    <p>Press <b>Report an issue</b> at the top of Help. It records which screen you're on, your app
    version and browser, and you can attach a screenshot. Everyone can see what's already been reported
    under <b>Help › Issues reported</b>, so you can tell whether the thing annoying you is already on
    the list. Owners and Directors mark them resolved. Bugs and ideas are both welcome.</p>` },
];

/**
 * v4.0.0 refresh (p38). Three columns: the contents with a reading-progress
 * bar and a tick per section read, the text, and a figure beside the first
 * three sections. The guide pages three sections at a time and each page ends
 * with "Next" (the design draws 01–03 then the Next bar); "In detail" is the
 * last page. Print shows every page.
 */
const READ_KEY = 'guide-read';
const PER_PAGE = 3;
// A one-line teaser for each section, shown on the Next bar that leads to it.
const TEASER = {
  4: 'how work arrives and gets priced.', 5: 'the container and the billable unit.', 6: 'the week, the lock and who approves.',
  7: 'your inbox, filed to projects.', 8: 'sending from your Gmail, and asking first.', 9: 'marking up a drawing without touching it.',
  10: 'invoices, orders and the bank.', 11: 'the people Track keeps track of.', 12: 'leave, site visits and who is away.',
  13: 'the box in the corner, and when it says no.', 14: 'who sees what, and the small rules.',
};
// The walkthrough that shows each numbered section on the real screen.
const WATCH = { 1: 'home-morning', 2: 'tasks-plan', 3: 'project-read', 4: 'enquiry-triage', 5: 'projects-find', 6: 'time-log',
  7: 'mail-file', 9: 'drawing-review', 10: 'finance-invoices', 11: 'clients-find', 12: 'calendar-leave', 13: 'ask-track' };
const PLAY = '<span class="gd4-playdot" aria-hidden="true"><svg width="8" height="8" viewBox="0 0 12 12"><path d="M3.5 2l6 4-6 4z" fill="currentColor"/></svg></span>';
const ICO = {
  sun: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="3.5"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4"/></svg>',
  clock: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="8"/><path d="M12 8v4l3 2"/></svg>',
  lock: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 018 0v3"/></svg>',
};

function readSet() { try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]')); } catch { return new Set(); } }
function saveRead(set) { try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch { /* per device */ } }

/** The figures beside 01–03: illustrative, sample data, static. */
function figure(n, steps) {
  if (n === 1) return `<figure class="gd4-fig">
      <div class="gd4-shot"><span class="gd4-shot-chrome"><i></i><i></i><i></i></span>
        <img src="/assets/help/t-home-v4.webp" alt="Home with sample data: the Needs you list" loading="lazy">
        <span class="gd4-shot-ring"></span></div>
      <figcaption>Home, with sample data. The ring marks Needs you.</figcaption></figure>`;
  if (n === 2 && steps) {
    const ico = [ICO.sun, ICO.clock, ICO.lock];
    return `<figure class="gd4-fig"><div class="gd4-card">${steps.map(([k, v], i) => `
      <div class="gd4-day"><span class="gd4-day-ico${i === 2 ? ' attn' : ''}">${ico[i] || ICO.clock}</span>
        <span><b>${escapeHtml(k)}</b><span>${escapeHtml(v)}</span></span></div>`).join('')}</div></figure>`;
  }
  if (n === 3) {
    const nodes = [['Enquiry', 'not linked', 'miss'], ['Fee', '£950', 'ok'], ['Instruction', 'missing · add it', 'fix'], ['Work', 'no time yet', 'miss'],
      ['Deadline', 'Wed 28 Oct', 'ok'], ['Issue', 'nothing yet', 'miss'], ['Invoice', '£475', 'ok']];
    return `<figure class="gd4-fig"><div class="gd4-card">
      <div class="gd4-gt-head"><b>8601 Mr &amp; Mrs Patel</b><span>3 of 7 linked</span></div>
      <div class="gd4-gt">${nodes.map(([k, v, st]) => `<div class="gd4-gt-row ${st}"><span class="gd4-node"></span><span class="gd4-gt-k">${k}</span><span class="gd4-gt-v">${v}</span></div>`).join('')}</div>
      <div class="gd4-legend"><span class="ok"><i></i>Exists</span><span class="miss"><i></i>Missing</span><span class="fix"><i></i>Needs fixing</span></div>
    </div><figcaption>8601 has been invoiced but has no instruction, so the instruction point is orange until one is added. Sample data.</figcaption></figure>`;
  }
  return '';
}

export function renderGuide(mount, session, { onGo, section: startSection = null } = {}) {
  const domain = orgSetting('signInDomain', '') || 'company';
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen'];
  const total = GUIDE.length;
  const pages = [];
  for (let i = 0; i < total; i += PER_PAGE) pages.push(GUIDE.slice(i, i + PER_PAGE).map((g, k) => i + k + 1));
  const detailPage = pages.length;            // "In detail" is the page after the numbered ones
  const roles = (session && session.roles) || [];
  const canWatch = (id) => {
    const w = WALKTHROUGHS.find((x) => x.id === id);
    if (!w) return null;
    if (w.who === 'calc' && !canCalculate(session?.email)) return null;
    if (w.who && w.who !== 'calc') { const item = navItem(w.who); if (item && !canSee(item, roles, session?.email)) return null; }
    return w;
  };
  const sub = (t) => String(t).replace(/\{domain\}/g, domain);
  const read = readSet();
  // The company's own host and domain, never ASP's literally (D5).
  const detailBody = (html) => html
    .replace(/track\.aspconsulting\.co\.uk/g, escapeHtml(location.host || 'Track'))
    .replace(/aspconsulting\.co\.uk/g, escapeHtml(domain === 'company' ? 'your company’s domain' : domain))
    .replace(/your ASP\s+Google account/g, 'your company Google account');

  const sectionHtml = (n) => {
    const [title, paras, steps, rule, tryLabel] = GUIDE[n - 1];
    const w = WATCH[n] ? canWatch(WATCH[n]) : null;
    const fig = figure(n, steps);
    return `<section class="gd4-sec${fig ? ' has-fig' : ''}" id="guide-${n}" data-n="${n}">
      <div class="gd4-text">
        <span class="gd4-n">${String(n).padStart(2, '0')}</span>
        <h2 class="gd4-h2">${escapeHtml(title)}</h2>
        ${paras.map((p) => `<p class="gd4-p">${fillKeys(escapeHtml(sub(p)))}</p>`).join('')}
        ${steps && n !== 2 ? `<dl class="gd4-kv">${steps.map(([k, v]) => `<div><dt>${fillKeys(escapeHtml(k))}</dt><dd>${fillKeys(escapeHtml(sub(v)))}</dd></div>`).join('')}</dl>` : ''}
        ${rule ? `<div class="gd4-rule"><b>The rule</b><span>${fillKeys(escapeHtml(sub(rule)))}</span></div>` : ''}
        <div class="gd4-acts">
          ${tryLabel ? `<button type="button" class="btn" data-try="${n - 1}">${escapeHtml(tryLabel)} &rarr;</button>` : ''}
          ${w ? `<button type="button" class="btn" data-watch="${escapeHtml(w.id)}">${PLAY}Watch · ${escapeHtml(durationLabel(walkthroughSecs(w)))}</button>` : ''}
        </div>
        <span class="gd4-end" aria-hidden="true"></span>
      </div>
      ${fig}
    </section>`;
  };

  const nextBar = (p) => {
    const nextN = p + 1 < pages.length ? pages[p + 1][0] : null;
    if (nextN) return `<div class="gd4-next"><span>Next: <b>${String(nextN).padStart(2, '0')} ${escapeHtml(GUIDE[nextN - 1][0])}</b>${TEASER[nextN] ? `, ${escapeHtml(TEASER[nextN])}` : ''}</span><button type="button" class="btn primary" data-page="${p + 1}">Next section &rarr;</button></div>`;
    if (p === pages.length - 1) return `<div class="gd4-next"><span>Next: <b>In detail</b>, the operational specifics: the email keys, the file formats, what moves status.</span><button type="button" class="btn primary" data-page="${detailPage}">Next section &rarr;</button></div>`;
    return '';
  };

  // The detail half. Its bodies are trusted literals written in this repo, so
  // they render as markup — unlike GUIDE above, which is prose and is escaped.
  // The company's own addresses come from its sign-in domain (D5).
  const detail = SECTIONS.map((s) => `
    <section class="gd4-sec detail" id="guide-d-${escapeHtml(s.id)}">
      <div class="gd4-text"><h2 class="gd4-h2">${escapeHtml(s.title.replace(/ & /g, ' and '))}</h2>
      <div class="gd4-body">${detailBody(s.body)}</div></div>
    </section>`).join('');

  mount.innerHTML = `
    <div class="gd4">
      <nav class="crumbs" aria-label="Breadcrumb"><button type="button" data-crumb>Help</button><span>/</span><span>Guide</span></nav>
      <div class="gd4-top">
        <div class="gd4-titles">
          <h1 class="gd4-h1">How to use Track</h1>
          <p class="gd4-sub">${escapeHtml(words[total] || String(total))} short sections, read top to bottom: what arrives, what it becomes, who decides and where the money goes. About 12 minutes.</p>
        </div>
        <div class="gd4-top-acts">
          ${canWatch('home-morning') ? `<button type="button" class="btn" data-watch="home-morning">${PLAY}Watch instead</button>` : ''}
          <button type="button" class="btn" data-print>Print</button>
        </div>
      </div>
      <div class="gd4-grid">
        <nav class="gd4-toc" aria-label="Contents">
          <label class="gd4-toc-select"><span class="gd4-toc-label">Contents</span>
            <select class="f-select" data-jump>${GUIDE.map(([t], i) => `<option value="guide-${i + 1}">${String(i + 1).padStart(2, '0')} ${escapeHtml(t)}</option>`).join('')}
              ${SECTIONS.map((s2) => `<option value="guide-d-${escapeHtml(s2.id)}">${escapeHtml(s2.title.replace(/ & /g, ' and '))}</option>`).join('')}</select></label>
          <div class="gd4-toc-list">
            <span class="gd4-toc-label">The guide</span>
            <span class="gd4-progress" role="progressbar" aria-label="Sections read" aria-valuemin="0" aria-valuemax="${total}"><span></span></span>
            ${GUIDE.map(([t], i) => `<a class="gd4-toc-item" href="#guide-${i + 1}" data-target="guide-${i + 1}"><span class="gd4-toc-n">${String(i + 1).padStart(2, '0')}</span><span class="gd4-toc-t">${escapeHtml(t)}</span><span class="gd4-tick" aria-hidden="true">✓</span></a>`).join('')}
            <span class="gd4-toc-label gd4-toc-detail">In detail</span>
            ${SECTIONS.map((s2) => `<a class="gd4-toc-sub" href="#guide-d-${escapeHtml(s2.id)}" data-target="guide-d-${escapeHtml(s2.id)}">${escapeHtml(s2.title.replace(/ & /g, ' and '))}</a>`).join('')}
          </div>
        </nav>
        <div class="gd4-main">
          ${pages.map((ns, p) => `<div class="gd4-page" data-pg="${p}" hidden>${ns.map(sectionHtml).join('')}${nextBar(p)}</div>`).join('')}
          <div class="gd4-page" data-pg="${detailPage}" hidden><div class="gd4-detail-h">In detail</div>${detail}</div>
        </div>
      </div>
    </div>`;

  const pageOf = (id) => {
    if (id.startsWith('guide-d-')) return detailPage;
    const n = Number(id.replace('guide-', ''));
    return Number.isFinite(n) && n >= 1 ? Math.floor((n - 1) / PER_PAGE) : 0;
  };
  let current = -1;
  const paintToc = (activeId) => {
    mount.querySelectorAll('.gd4-toc-item').forEach((a) => {
      const n = Number(a.dataset.target.replace('guide-', ''));
      a.classList.toggle('read', read.has(n));
      a.classList.toggle('on', a.dataset.target === activeId);
      if (a.dataset.target === activeId) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    const bar = mount.querySelector('.gd4-progress');
    bar.setAttribute('aria-valuenow', String(read.size));
    bar.firstElementChild.style.width = `${Math.min(100, (read.size / total) * 100).toFixed(1)}%`;
  };
  let io = null;
  const showPage = (p, scrollTo = null) => {
    if (p !== current) {
      current = p;
      mount.querySelectorAll('.gd4-page').forEach((el) => { el.hidden = Number(el.dataset.pg) !== p; });
      // "Read" = the end of a section has passed the reading line once.
      io?.disconnect();
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver((entries) => entries.forEach((en) => {
          if (!en.isIntersecting) return;
          const n = Number(en.target.closest('.gd4-sec')?.dataset.n);
          if (n && !read.has(n)) { read.add(n); saveRead(read); paintToc(firstOn()); }
        }), { rootMargin: '0px 0px -30% 0px' });
        mount.querySelectorAll(`.gd4-page[data-pg="${p}"] .gd4-end`).forEach((e) => io.observe(e));
      }
    }
    const target = scrollTo ? mount.querySelector(`#${CSS.escape(scrollTo)}`) : null;
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (target) requestAnimationFrame(() => target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' }));
    paintToc(scrollTo || firstOn());
  };
  const firstOn = () => { const sec = mount.querySelector(`.gd4-page[data-pg="${current}"] .gd4-sec`); return sec ? sec.id : ''; };
  const jump = (id) => {
    showPage(pageOf(id), id);
    try { history.replaceState(history.state, '', `#${id}`); } catch { /* fine */ }
  };

  mount.querySelector('[data-crumb]').addEventListener('click', () => onGo && onGo('help'));
  mount.querySelectorAll('[data-target]').forEach((a) => a.addEventListener('click', (ev) => { ev.preventDefault(); jump(a.dataset.target); }));
  mount.querySelector('[data-jump]').addEventListener('change', (e) => jump(e.target.value));
  mount.querySelectorAll('[data-page]').forEach((b) => b.addEventListener('click', () => {
    const p = Number(b.dataset.page);
    const first = p === detailPage ? `guide-d-${SECTIONS[0].id}` : `guide-${pages[p][0]}`;
    jump(first);
  }));
  mount.querySelector('[data-print]').addEventListener('click', () => window.print());
  mount.querySelectorAll('[data-watch]').forEach((b) => b.addEventListener('click', () => {
    const w = canWatch(b.dataset.watch);
    if (w) openWalkthrough({ ...w, steps: w.steps }, { onGo: (v) => onGo && onGo(v) });
  }));
  mount.querySelectorAll('[data-try]').forEach((b) => b.addEventListener('click', () => {
    const target = GUIDE[Number(b.dataset.try)][5];
    if (target && onGo) onGo(target);
    // A section that names no screen (Ask Track): its subject is the Ask
    // panel, so the button opens it rather than navigating anywhere.
    else document.dispatchEvent(new CustomEvent('track:ask-open'));
  }));

  const fromHash = /^#guide-/.test(location.hash) ? location.hash.slice(1) : null;
  const start = startSection ? `guide-${startSection}` : fromHash;
  if (start && mount.querySelector(`#${CSS.escape(start)}`)) showPage(pageOf(start), start);
  else showPage(0);
}
